import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState, type FormEvent, type MouseEvent, type ReactNode } from 'react'
import { ApiError } from '../../api/client'
import {
  addAgentTemplate,
  addEmailTemplate,
  agentTemplatesQuery,
  companySettingsQuery,
  createEmailServer,
  createTrigger,
  deleteAgentTemplate,
  deleteEmailTemplate,
  deleteTrigger,
  emailServersQuery,
  emailTemplatesQuery,
  isProactiviteeTab,
  nsclientQuery,
  proactiviteeShellQuery,
  quietDelete,
  quietDigestSave,
  quietEnd,
  quietHoursQuery,
  quietSave,
  removeEmailServer,
  saveEmailServer,
  standardCommandsQuery,
  triggerCreateFormQuery,
  triggerFormQuery,
  triggersQuery,
  updateAgentTemplate,
  updateCompanySettings,
  updateEmailTemplate,
  updateTrigger,
  uploadNsClient,
  type ProactiviteeTab,
} from '../../api/settingsProactivitee'
import { exact, stamp } from '../../inventoree/format'
import { toast } from '../../ui/notify'
/* Capul zonei si bifa ei - aceleasi piese ca pe Inventoree, Softsee, Opteemum. */
import { SettingsCheck, SettingsHead } from '../../ui/SettingsParts'
import { Spinner } from '../../ui/Spinner'
import { LiveAlarmsEditor } from './LiveAlarmsEditor'

/**
 * Settings → Proactivitee: index.php + fiecare ecran din
 * proactivitee_settings/, element cu element.
 */

export function ProactiviteeSettingsPage({ tab }: { tab: string }) {
  const navigate = useNavigate()
  const shell = useQuery(proactiviteeShellQuery())

  useEffect(() => {
    if (!shell.data) return
    if (tab !== '' && isProactiviteeTab(tab)) return
    const first = shell.data.tabs[0]
    if (first) void navigate({ to: '/settings/proactivitee/$tab', params: { tab: first.id } })
  }, [shell.data, tab, navigate])

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!shell.data) return <Spinner />

  const current = isProactiviteeTab(tab) ? tab : (shell.data.tabs[0]?.id ?? '')

  return (
    <>
      <aside className="od-dark-sidebar od-module-rail">
        <ul className="od-dark-sidebar-nav default-sidebar-nav">
          {shell.data.tabs.map((item, index) => (
            <SidebarBlock key={item.id} tab={item} current={current} previous={shell.data.tabs[index - 1]} />
          ))}
          <li className="menu-block-divider"></li>
        </ul>
      </aside>
      <div className="page-content-wrapper">
        {current === 'alarms' && <LiveAlarmsEditor />}
        {current === 'company_settings' && <CompanyPanel />}
        {current === 'nsclient' && <NsClientPanel />}
        {current === 'email_templates' && <EmailTemplatesPanel />}
        {current === 'agent_notification_templates' && <AgentTemplatesPanel />}
        {current === 'triggers_settings' && <TriggersPanel />}
        {current === 'quiet_hours' && <QuietHoursPanel />}
        {current === 'email_server_settings' && <EmailServersPanel />}
      </div>
    </>
  )
}

function SidebarBlock({ tab, current, previous }: { tab: ProactiviteeTab; current: string; previous?: ProactiviteeTab }) {
  return (
    <>
      {tab.header && (!previous || previous.header !== tab.header) ? (
        <li className={tab.lead ? 'menu-header menu-header-lead' : 'menu-header'}>{tab.header}</li>
      ) : null}
      <SidebarTab tab={tab} current={current} />
    </>
  )
}

function SidebarTab({ tab, current }: { tab: ProactiviteeTab; current: string }) {
  const navigate = useNavigate()
  const active = tab.id === current
  const href = '/app/settings/proactivitee/' + tab.id
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    void navigate({ to: '/settings/proactivitee/$tab', params: { tab: tab.id } })
  }
  return (
    <li className={active ? 'menu-tab active' : 'menu-tab'}>
      <a href={href} onClick={onClick}>
        <i className={tab.icon} aria-hidden="true"></i> {tab.label}
      </a>
    </li>
  )
}

function num(row: Record<string, unknown> | null | undefined, key: string): number {
  return Number(row?.[key] ?? 0)
}

function str(row: Record<string, unknown> | null | undefined, key: string): string {
  return String(row?.[key] ?? '')
}

/**
 * Un rand de ales, in desenul zonei: butonul nativ al consolei lipit de
 * eticheta lui intr-un singur `<label>`, exact ca bifa comuna
 * (`SettingsCheck`). Bifa are piesa ei in `ui/SettingsParts.tsx`; radioul nu
 * are, si nu se adauga una acolo pentru un singur ecran - dar poarta aceeasi
 * clasa, deci aceeasi asezare.
 *
 * Clasa `icheck-radio` pe care o purtau pana acum nu e scrisa NICAIERI in v4:
 * ea pornea iCheck din `public/js/views/proactivitee_settings/company_settings/
 * index-1.js`, javascript care nu vine incoace. Ramasa singura, nu facea nimic
 * - iar bifele cadeau in schimb pe `.checkbox input[type="checkbox"]` din
 * Bootstrap (`position: absolute; margin-left: -20px`), adica alt desen decat
 * radiourile de deasupra lor, pe acelasi ecran.
 *
 * `marginRight: 0` scoate cei 6px pe care `od_console.css` ii da oricarui
 * `input[type="radio"]`: foaia comuna ii scoate pe ai bifei (`label.od-inv-check
 * > input[type="checkbox"] { margin: 0 }`), si fara asta radioul ar fi stat la
 * 12px de textul lui, iar bifa de alaturi la 6.
 */
function SettingsChoice({ name, value, label, defaultChecked }: { name: string; value: string; label: string; defaultChecked: boolean }) {
  return (
    <div className="checkbox">
      <label className="od-inv-check">
        <input type="radio" name={name} value={value} defaultChecked={defaultChecked} style={{ marginRight: 0 }} />
        <span>{label}</span>
      </label>
    </div>
  )
}

/**
 * Settings › Proactivitee › Company settings - `proactivitee_settings/
 * company_settings/index.php`.
 *
 * Trei lucruri fata de transcrierea de pana acum, toate vazute pe ecran:
 *
 *   - `hr.dotted` de la coada fiecarei sectiuni. In CI3 `section.settings` nu
 *     era o cutie, si linia punctata despartea sectiunile intre ele. In consola
 *     fiecare sectiune E un card, cu 18px de respiratie in el, deci linia
 *     desparte continutul de propria lui margine: o dunga si o banda de gol sub
 *     ea, in toate trei cardurile. Cardul desparte deja.
 *   - butonul Save statea intr-o a PATRA sectiune, adica intr-un card gol cu un
 *     buton in el. Acum sta sub formular, cum sta pe celelalte ecrane.
 *   - salvarea nu spunea nimic cand pica. `ajaxForm` din CI3 avea `error:` cu
 *     "Something went wrong." si stingea butonul cat tinea cererea; aici
 *     `await` nefericit iesea cu o promisiune respinsa si cu ecranul neschimbat
 *     - iar un om fara `settings.proactivitee.edit` chiar asta primeste, un 403
 *     tacut.
 */
function CompanyPanel() {
  const query = useQuery(companySettingsQuery())
  const client = useQueryClient()
  const [busy, setBusy] = useState(false)
  if (query.error instanceof ApiError && query.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!query.data) return <Spinner />
  const settings = query.data.company_settings
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    setBusy(true)
    try {
      await updateCompanySettings({
        settings_mode: String(form.get('settings_mode') ?? '0'),
        use_alarm_groups: String(form.get('use_alarm_groups') ?? '0'),
        proactive_alarms_support: form.get('proactive_alarms_support') ? 'on' : '',
        default_alarms_settings_no_group: form.get('default_alarms_settings_no_group') ? 'on' : '',
      })
      toast('Settings saved successfully.', 'Company settings', 'success')
      await client.invalidateQueries({ queryKey: ['settings', 'proactivitee', 'company'] })
    } catch {
      toast('Something went wrong.', 'Company settings', 'error')
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      <SettingsHead
        strong="Company"
        rest="settings"
        subtitle={
          <>
            Manage <strong>PROACTIVITEE settings</strong> for the selected company
          </>
        }
      />
      <form action="/settings/proactivitee/update_company_settings" method="post" id="updateProactiviteeSettingsForm" onSubmit={onSubmit}>
        <section className="settings">
          <div className="row">
            <div className="col-md-2">
              <h5 style={{ marginBottom: 20 }}>Module settings</h5>
            </div>
            <div className="col-md-10">
              <div className="form-group">
                <SettingsChoice name="settings_mode" value="0" label="Use default alarms settings" defaultChecked={num(settings, 'settings_mode') === 0} />
                <SettingsChoice name="settings_mode" value="1" label="Use company settings" defaultChecked={num(settings, 'settings_mode') === 1} />
                <SettingsChoice name="settings_mode" value="2" label="Use device settings" defaultChecked={num(settings, 'settings_mode') === 2} />
              </div>
            </div>
          </div>
        </section>
        <section className="settings">
          <div className="row">
            <div className="col-md-2">
              <h5 style={{ marginBottom: 20 }}>Proactive support</h5>
            </div>
            <div className="col-md-10">
              <div className="form-group">
                <div className="checkbox">
                  <SettingsCheck
                    name="proactive_alarms_support"
                    label="Enable proactive support for all devices"
                    defaultChecked={num(settings, 'proactive_alarms_support') === 1}
                  />
                </div>
                <span className="help-block">
                  This option takes effect only if <strong>Module settings</strong> mode is <strong>Use company settings</strong>.
                </span>
              </div>
            </div>
          </div>
        </section>
        <section className="settings">
          <div className="row">
            <div className="col-md-2">
              <h5 style={{ marginBottom: 20 }}>Alarms settings</h5>
            </div>
            <div className="col-md-10">
              <div className="form-group">
                <SettingsChoice
                  name="use_alarm_groups"
                  value="0"
                  label="Use default alarms settings for all devices"
                  defaultChecked={num(settings, 'use_alarm_groups') === 0}
                />
                <SettingsChoice name="use_alarm_groups" value="1" label="Use alarms groups" defaultChecked={num(settings, 'use_alarm_groups') === 1} />
                <div className="checkbox" style={{ marginLeft: 30 }}>
                  <SettingsCheck
                    name="default_alarms_settings_no_group"
                    label="Use default alarms settings if device does not have a group of alarms assigned"
                    defaultChecked={num(settings, 'default_alarms_settings_no_group') === 1}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
        <div className="form-group">
          <button type="submit" className="btn btn-sm btn-primary" disabled={busy}>
            <i className="fa fa-check" aria-hidden="true"></i> Save
          </button>
        </div>
      </form>
    </>
  )
}

function NsClientPanel() {
  const query = useQuery(nsclientQuery())
  const client = useQueryClient()
  if (!query.data) return <Spinner />
  const file = query.data.nsclient_file
  const onUpload = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const res = await uploadNsClient(form)
    if (res.ok) toast('File uploaded.', 'NS Client', 'success')
    else toast(res.message ?? 'File could not be uploaded! Please try again..', 'NS Client', 'error')
    await client.invalidateQueries({ queryKey: ['settings', 'proactivitee', 'nsclient'] })
  }
  return (
    <>
      <h4>Upload <strong>NS Client</strong></h4>
      <hr />
      <form className="form-horizontal fv-form fv-form-bootstrap labelcustomize" id="uploadNsClient" method="post" onSubmit={onUpload}>
        <input type="hidden" name="service_provider" value={String(query.data.service_provider)} />
        <div className="col-sm-3 selectContainer">
          <input type="file" className="form-control" name="nsclient" />
        </div>
        <button type="submit" className="btn btn-sm btn-primary uploadNsClient">{file ? 'Change' : 'Upload'}</button>
        {file ? (
          <div className="col-sm-12">
            <p>Current file: <b>{file.name}</b></p>
          </div>
        ) : null}
      </form>
    </>
  )
}

/** `moment(last_updated).format('MMM Do YYYY, HH:mm')` din `settings/proactivitee.js`. */
function updatedLabel(value: unknown): string {
  const when = stamp(value)
  if (!when) return ''
  /* `exact()` scrie si secundele; lista veche se opreste la minut. */
  return exact(when).replace(/:\d{2}$/, '')
}

/** `body.replace(/<[^>]+>/g, '').substring(0, 60) + '...'`, ca in `DisplayList()`. */
function bodyExcerpt(body: string): string {
  return body.replace(/<[^>]+>/g, '').substring(0, 60) + '...'
}

function EmailTemplatesPanel() {
  return <TemplatesPanel kind="email" />
}

function AgentTemplatesPanel() {
  return <TemplatesPanel kind="agent" />
}

/**
 * Cele doua ecrane de sabloane, element cu element fata de `EmailTemplatesPage`
 * si `AgentNotificationTemplates` din `public/js/settings/proactivitee.js`:
 * acelasi tabel cu cinci coloane, acelasi rezumat de 60 de caractere, aceeasi
 * data trecuta prin `moment`, si aceleasi doua formulare cu simbolurile
 * alaturi.
 *
 * Capetele de API sunt aceleasi pentru amandoua (`template_name`,
 * `template_subject`, `template_body`); difera numai coloanele randului adus
 * inapoi si etichetele formularului.
 */
type TemplateSpec = {
  title: ReactNode
  subtitle: ReactNode
  contentId: string
  createId: string
  createFormId: string
  updateFormId: string
  bodyId: string
  editBodyId: string
  bodyClass: string
  rowIcon: string
  subjectField: string
  bodyField: string
  subjectLabel: string
  bodyLabel: string
  toastTitle: string
  queryKey: string[]
}

const TEMPLATE_SPEC: Record<'email' | 'agent', TemplateSpec> = {
  email: {
    title: (
      <>
        <strong>Email templates</strong> notifications
      </>
    ),
    subtitle: (
      <>
        Create and edit PROACTIVITEE <strong>email templates</strong> notifications
      </>
    ),
    contentId: 'email-templates-content',
    createId: 'create-email-template',
    createFormId: 'create-email-template-form',
    updateFormId: 'update-email-template-form',
    bodyId: 'compose_email',
    editBodyId: 'edit_email',
    bodyClass: 'col-md-12',
    rowIcon: 'far fa-envelope-open',
    subjectField: 'email_subject',
    bodyField: 'email_body',
    subjectLabel: 'Email subject',
    bodyLabel: 'Email body',
    toastTitle: 'Email template',
    queryKey: ['settings', 'proactivitee', 'email-templates'],
  },
  agent: {
    title: (
      <>
        Agent notification <strong>templates</strong>
      </>
    ),
    subtitle: (
      <>
        Create and edit PROACTIVITEE <strong>agent notification templates</strong>
      </>
    ),
    contentId: 'agent-notification-templates-content',
    createId: 'create-agent-notification-template',
    createFormId: 'create-agent-notification-template-form',
    updateFormId: 'update-agent-notification-template-form',
    bodyId: 'compose_template',
    editBodyId: 'edit_template',
    bodyClass: 'form-control',
    /*
     * Portalul scrie `fa fa-commenting-o` - nume de Font Awesome 4. v4
     * serveste Font Awesome 5.15, unde numele ala NU EXISTA, deci ar iesi o
     * casuta goala. `far fa-comment-dots` e acelasi desen in 5.
     */
    rowIcon: 'far fa-comment-dots',
    subjectField: 'notification_subject',
    bodyField: 'notification_body',
    subjectLabel: 'Notification subject',
    bodyLabel: 'Notification body',
    toastTitle: 'Agent notification template',
    queryKey: ['settings', 'proactivitee', 'agent-templates'],
  },
}

function TemplatesPanel({ kind }: { kind: 'email' | 'agent' }) {
  const spec = TEMPLATE_SPEC[kind]
  const query = useQuery(kind === 'email' ? emailTemplatesQuery() : agentTemplatesQuery())
  const client = useQueryClient()
  const [edited, setEdited] = useState<Record<string, unknown> | null>(null)
  const [creating, setCreating] = useState(false)
  if (!query.data) return <Spinner />

  const templates = query.data.templates
  const reload = () => client.invalidateQueries({ queryKey: spec.queryKey })

  const onRemove = async (id: number) => {
    if (!confirm('Are you sure? Data will be lost.')) return
    if (kind === 'email') await deleteEmailTemplate(id)
    else await deleteAgentTemplate(id)
    toast('Template deleted.', spec.toastTitle, 'success')
    await reload()
  }

  const head = (
    <>
      <h4>{spec.title}</h4>
      <label className="color" style={{ marginBottom: 0 }}>
        {spec.subtitle}
      </label>
    </>
  )

  if (creating || edited !== null) {
    return (
      <>
        {head}
        <hr />
        <div id={spec.contentId}>
          <TemplateForm
            spec={spec}
            kind={kind}
            template={edited}
            variables={query.data.variables}
            onCancel={() => {
              setCreating(false)
              setEdited(null)
            }}
            onSaved={async () => {
              setCreating(false)
              setEdited(null)
              await reload()
            }}
          />
        </div>
      </>
    )
  }

  return (
    <>
      {head}
      <button className="btn btn-success pull-right" id={spec.createId} type="button" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create template
      </button>
      <hr />
      <div id={spec.contentId}>
        <table className="table table-hover">
          <thead>
            <tr>
              <th className="align-left">Template name</th>
              <th className="align-left">Email subject</th>
              <th className="align-left">Email body</th>
              <th className="align-left">Last Update</th>
              <th className="align-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.length === 0 ? (
              <tr className="align-center font-14" style={{ borderBottom: '1px solid #ddd' }}>
                <td colSpan={5}>
                  <i className="fas fa-info-circle" aria-hidden="true"></i> No template to show
                </td>
              </tr>
            ) : (
              templates.map((row) => (
                <tr key={String(row.ID)}>
                  <td>
                    <h6>
                      <i className={spec.rowIcon} aria-hidden="true"></i> {str(row, 'template_name')}
                    </h6>
                  </td>
                  <td>{str(row, spec.subjectField)}</td>
                  <td>{bodyExcerpt(str(row, spec.bodyField))}</td>
                  <td>{updatedLabel(row.last_updated)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary edit-template"
                      data-template={String(row.ID)}
                      onClick={() => setEdited(row)}
                    >
                      Edit
                    </button>{' '}
                    <button
                      type="button"
                      className="btn btn-sm btn-danger remove-template"
                      data-template={String(row.ID)}
                      onClick={() => void onRemove(num(row, 'ID'))}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

/**
 * `create.php` / `update.php`: aceleasi campuri, cu simbolurile in dreapta.
 *
 * CUPRINSUL SE SCRIE INTR-O CASUTA, NU IN CKEDITOR. In portal cuprinsul de
 * email trecea prin CKEditor, o biblioteca jQuery care isi scrie singura
 * DOM-ul si nu se impaca cu React - la fel ca pe Settings > Easeedesk > Email
 * templates, pana cand editorul bogat isi capata perechea in v4. Ce se
 * salveaza e acelasi HTML.
 */
function TemplateForm({
  spec,
  kind,
  template,
  variables,
  onCancel,
  onSaved,
}: {
  spec: TemplateSpec
  kind: 'email' | 'agent'
  template: Record<string, unknown> | null
  variables: Array<Record<string, unknown>>
  onCancel(): void
  onSaved(): Promise<void>
}) {
  const [name, setName] = useState(template === null ? '' : str(template, 'template_name'))
  const [subject, setSubject] = useState(template === null ? '' : str(template, spec.subjectField))
  const [body, setBody] = useState(template === null ? '' : str(template, spec.bodyField))
  const [saving, setSaving] = useState(false)
  const bodyRef = useRef<HTMLTextAreaElement>(null)

  /** `.insert-body-variable`: simbolul intra acolo unde e cursorul. */
  const insert = (token: string) => {
    const area = bodyRef.current
    if (!area) {
      setBody((current) => current + token)
      return
    }
    const start = area.selectionStart
    const end = area.selectionEnd
    setBody((current) => current.slice(0, start) + token + current.slice(end))
    requestAnimationFrame(() => {
      area.focus()
      area.setSelectionRange(start + token.length, start + token.length)
    })
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSaving(true)
    const fields: Record<string, string> = {
      template_name: name,
      template_subject: subject,
      template_body: body,
    }
    try {
      if (template === null) {
        if (kind === 'email') await addEmailTemplate(fields)
        else await addAgentTemplate(fields)
      } else {
        fields.template = String(template.ID)
        if (kind === 'email') await updateEmailTemplate(fields)
        else await updateAgentTemplate(fields)
      }
      toast(
        template === null ? 'Template created successfully' : 'Template updated successfully',
        spec.toastTitle,
        'success',
      )
      await onSaved()
    } catch (error) {
      setSaving(false)
      toast(
        error instanceof ApiError && error.message ? error.message : 'Something went wrong',
        spec.toastTitle,
        'error',
      )
    }
  }

  return (
    <form id={template === null ? spec.createFormId : spec.updateFormId} method="post" onSubmit={onSubmit}>
      {template === null ? null : <input type="hidden" name="template" value={String(template.ID)} readOnly />}
      <div className="form-group">
        <label>Template name</label>
        <input
          className="form-control"
          type="text"
          name="template_name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>{spec.subjectLabel}</label>
        <input
          className="form-control"
          type="text"
          name="template_subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
      </div>
      <div className="form-group">
        <div className="row">
          <div className="col-md-8">
            <label>{spec.bodyLabel}</label>
            <textarea
              ref={bodyRef}
              style={{ maxWidth: '100%' }}
              className={spec.bodyClass}
              id={template === null ? spec.bodyId : spec.editBodyId}
              name="template_body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            ></textarea>
          </div>
          <div className="col-md-4">
            <label>Variables</label>
            <div>
              {variables.map((variable) => (
                <button
                  key={str(variable, 'variable')}
                  type="button"
                  className="btn btn-default btn-sm insert-body-variable"
                  data-variable={str(variable, 'variable')}
                  onClick={() => insert(str(variable, 'variable'))}
                >
                  {str(variable, 'variable_name')}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <button
        className="btn btn-default"
        type="button"
        id={template === null ? 'cancel-create-template' : 'cancel-update-template'}
        onClick={onCancel}
      >
        Cancel
      </button>{' '}
      <button className="btn btn-primary" type="submit" disabled={saving}>
        {template === null ? 'Create' : 'Update'}
      </button>
    </form>
  )
}

/**
 * Settings > Proactivitee > Alarms triggers, dupa `AlarmTriggers` din
 * `public/js/settings/proactivitee.js`: tabelul cu faptele bifate pe fiecare
 * declansator, si formularul in care fiecare fapt isi are casuta lui, dupa
 * `action_key` - adrese de email, sablon de agent, sablon de client, comenzi.
 */
function TriggersPanel() {
  const query = useQuery(triggersQuery())
  const client = useQueryClient()
  const [open, setOpen] = useState<'list' | 'create' | number>('list')
  if (!query.data) return <Spinner />

  const reload = () => client.invalidateQueries({ queryKey: ['settings', 'proactivitee', 'triggers'] })

  const onRemove = async (id: number) => {
    if (!confirm('Are you sure? Data will be lost.')) return
    await deleteTrigger(id)
    toast('Trigger deleted.', 'Trigger', 'success')
    await reload()
  }

  const head = (
    <>
      <h4>
        <strong>Alarm</strong> groups
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Create and edit PROACTIVITEE <strong>alarm triggers</strong>
      </label>
    </>
  )

  if (open !== 'list') {
    return (
      <>
        {head}
        <hr />
        <div id="triggers-content">
          <TriggerForm
            triggerId={open === 'create' ? null : open}
            onCancel={() => setOpen('list')}
            onSaved={async () => {
              setOpen('list')
              await reload()
            }}
          />
        </div>
      </>
    )
  }

  return (
    <>
      {head}
      <button className="btn btn-success pull-right" id="create-trigger" type="button" onClick={() => setOpen('create')}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create trigger
      </button>
      <hr />
      <div id="triggers-content">
        <table className="table table-hover" style={{ backgroundColor: '#fff' }}>
          <thead>
            <tr>
              <th className="align-left">Trigger name</th>
              <th className="align-left">Trigger actions</th>
              <th className="align-left">Last Update</th>
              <th className="align-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {query.data.triggers.length === 0 ? (
              <tr className="align-center font-14" style={{ borderBottom: '1px solid #ddd' }}>
                <td colSpan={5}>
                  <i className="fas fa-info-circle" aria-hidden="true"></i> No triggers to show
                </td>
              </tr>
            ) : (
              query.data.triggers.map((row) => {
                const actions = Array.isArray(row.actions) ? (row.actions as Array<Record<string, unknown>>) : []
                return (
                  <tr key={String(row.ID)}>
                    <td>
                      <h6>
                        <i className="fas fa-bolt" aria-hidden="true"></i> {str(row, 'trigger_name')}
                      </h6>
                    </td>
                    <td>
                      {actions.length === 0 ? null : (
                        <ul>
                          {actions.map((action, index) => (
                            <li key={index} className="color-green2">
                              <i className="fas fa-check" aria-hidden="true"></i> {str(action, 'action_name')}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td>{updatedLabel(row.last_updated)}</td>
                    <td className="align-right">
                      <button
                        type="button"
                        className="btn btn-sm btn-primary edit-trigger"
                        data-trigger={String(row.ID)}
                        onClick={() => setOpen(num(row, 'ID'))}
                      >
                        Edit
                      </button>{' '}
                      <button
                        type="button"
                        className="btn btn-sm btn-danger remove-trigger"
                        data-trigger={String(row.ID)}
                        onClick={() => void onRemove(num(row, 'ID'))}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

/** Ce tine un fapt bifat, cat sta formularul deschis. */
type ActionState = {
  status: boolean
  /** `values[]` - adresele de email, comenzile bifate. */
  values: string[]
  /** `values` - sablonul ales, cand faptul cere unul singur. */
  single: string
  emailServer: string
}

/** Faptele care cer o lista de valori, nu una singura. */
function isMultiValue(key: string): boolean {
  return key === 'send_email' || key === 'send_automated_commands'
}

/** Faptele care pleaca printr-un server de email. */
function needsEmailServer(key: string): boolean {
  return key === 'send_email' || key === 'customer_email'
}

/** `action_values` din baza: o lista se tine ca JSON, restul ca text. */
function parseActionValues(raw: unknown): { values: string[]; single: string } {
  const text = raw === null || raw === undefined ? '' : String(raw)
  if (text === '') return { values: [''], single: '' }
  if (text.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(text)
      if (Array.isArray(parsed)) {
        const values = parsed.map((item) => String(item))
        return { values: values.length === 0 ? [''] : values, single: '' }
      }
    } catch {
      /* Text obisnuit care incepe cu paranteza: se ia ca atare. */
    }
  }
  return { values: [text], single: text }
}

function TriggerForm({
  triggerId,
  onCancel,
  onSaved,
}: {
  triggerId: number | null
  onCancel(): void
  onSaved(): Promise<void>
}) {
  const form = useQuery(triggerId === null ? triggerCreateFormQuery() : triggerFormQuery(triggerId))
  const emailTemplates = useQuery(emailTemplatesQuery())
  const agentTemplates = useQuery(agentTemplatesQuery())
  const commands = useQuery(standardCommandsQuery())
  const [name, setName] = useState('')
  const [state, setState] = useState<Record<number, ActionState>>({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!form.data) return
    setName(str(form.data.trigger ?? null, 'trigger_name'))
    const next: Record<number, ActionState> = {}
    for (const action of form.data.actions) {
      const parsed = parseActionValues(action.action_values)
      next[Number(action.ID)] = {
        status: Number(action.action_status ?? 0) === 1,
        values: parsed.values,
        single: parsed.single,
        emailServer: action.email_server_id ? String(action.email_server_id) : '',
      }
    }
    setState(next)
  }, [form.data])

  if (!form.data) return <Spinner />

  const actions = form.data.actions
  const set = (id: number, patch: Partial<ActionState>) =>
    setState((current) => ({ ...current, [id]: { ...current[id], ...patch } }))

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSaving(true)
    const fields: Record<string, string | string[]> = { trigger_name: name }
    if (triggerId !== null) fields.trigger_id = String(triggerId)
    for (const action of actions) {
      const id = Number(action.ID)
      const current = state[id]
      if (!current) continue
      const key = 'trigger_actions[' + id + ']'
      const actionKey = action.action_key ?? ''
      if (current.status) fields[key + '[status]'] = 'on'
      if (isMultiValue(actionKey)) {
        const values = current.values.filter((value) => value.trim() !== '')
        if (values.length > 0) fields[key + '[values][]'] = values
      } else if (current.single !== '') {
        fields[key + '[values]'] = current.single
      }
      if (needsEmailServer(actionKey) && current.emailServer !== '') {
        fields[key + '[email_server]'] = current.emailServer
      }
    }
    try {
      if (triggerId === null) await createTrigger(fields)
      else await updateTrigger(fields)
      toast(triggerId === null ? 'Trigger created successfully' : 'Trigger updated successfully', 'Trigger', 'success')
      await onSaved()
    } catch (error) {
      setSaving(false)
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong', 'Trigger', 'error')
    }
  }

  return (
    <form id={triggerId === null ? 'create-trigger-form' : 'update-trigger-form'} method="post" onSubmit={onSubmit}>
      {triggerId === null ? null : <input type="hidden" name="trigger_id" value={String(triggerId)} readOnly />}
      <div className="form-group">
        <label>Trigger name</label>
        <input
          className="form-control"
          type="text"
          name="trigger_name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      {actions.map((action) => {
        const id = Number(action.ID)
        const current = state[id] ?? { status: false, values: [''], single: '', emailServer: '' }
        return (
          <div key={id}>
            <hr className="dotted" />
            <div className="row">
              <div className="col-md-3 left">
                <h5 style={{ marginBottom: 20 }}>{action.action_name ?? ''}</h5>
                {/*
                  In portal bifa asta era un `js-switch` desenat de Switchery -
                  o biblioteca jQuery pe care v4 n-o incarca, deci comutatorul
                  ar fi ramas o casuta moarta. Ramane casuta obisnuita, cu
                  acelasi nume de camp la POST.
                */}
                <input
                  type="checkbox"
                  className="trigger-action-status"
                  name={'trigger_actions[' + id + '][status]'}
                  checked={current.status}
                  onChange={(e) => set(id, { status: e.target.checked })}
                />
              </div>
              <div className="col-md-9 right">
                <TriggerActionInput
                  actionId={id}
                  actionKey={action.action_key ?? ''}
                  state={current}
                  servers={form.data.email_servers}
                  emailTemplates={emailTemplates.data?.templates ?? []}
                  agentTemplates={agentTemplates.data?.templates ?? []}
                  commands={commands.data?.commands ?? []}
                  set={set}
                />
              </div>
            </div>
          </div>
        )
      })}
      <hr className="dotted" />
      <button className="btn btn-default" type="button" id="cancel-trigger-action" onClick={onCancel}>
        Cancel
      </button>{' '}
      <button className="btn btn-primary" type="submit" disabled={saving}>
        {triggerId === null ? 'Create' : 'Update'}
      </button>
    </form>
  )
}

/** `actionInput()` din portal: casuta din dreapta, dupa `action_key`. */
function TriggerActionInput({
  actionId,
  actionKey,
  state,
  servers,
  emailTemplates,
  agentTemplates,
  commands,
  set,
}: {
  actionId: number
  actionKey: string
  state: ActionState
  servers: Array<Record<string, unknown>>
  emailTemplates: Array<Record<string, unknown>>
  agentTemplates: Array<Record<string, unknown>>
  commands: Array<Record<string, unknown>>
  set(id: number, patch: Partial<ActionState>): void
}) {
  const serverSelect = (
    <div className="form-group">
      <select
        className="form-control"
        name={'trigger_actions[' + actionId + '][email_server]'}
        value={state.emailServer}
        onChange={(e) => set(actionId, { emailServer: e.target.value })}
      >
        <option value="">Select email server</option>
        {servers.map((server) => (
          <option key={String(server.ID)} value={String(server.ID)}>
            {str(server, 'scheme_name')}
          </option>
        ))}
      </select>
    </div>
  )

  if (actionKey === 'send_email') {
    return (
      <div>
        {serverSelect}
        {state.values.map((value, index) => (
          <div className="input-group entry" key={index}>
            <input
              className="form-control"
              type="text"
              name={'trigger_actions[' + actionId + '][values][]'}
              placeholder="enter email address"
              value={value}
              onChange={(e) => {
                const next = state.values.slice()
                next[index] = e.target.value
                set(actionId, { values: next })
              }}
            />
            <span className="input-group-btn">
              <button
                type="button"
                className="btn btn-success add-input"
                onClick={() =>
                  set(actionId, {
                    values: [...state.values.slice(0, index + 1), '', ...state.values.slice(index + 1)],
                  })
                }
              >
                <i className="fas fa-plus" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                className="btn btn-danger remove-input"
                onClick={() => {
                  const next = state.values.filter((_, i) => i !== index)
                  set(actionId, { values: next.length === 0 ? [''] : next })
                }}
              >
                <i className="fas fa-minus" aria-hidden="true"></i>
              </button>
            </span>
          </div>
        ))}
      </div>
    )
  }

  if (actionKey === 'agent_notification' || actionKey === 'customer_email') {
    const options = actionKey === 'agent_notification' ? agentTemplates : emailTemplates
    return (
      <div className="form-group">
        {actionKey === 'customer_email' ? serverSelect : null}
        <select
          className="form-control"
          name={'trigger_actions[' + actionId + '][values]'}
          value={state.single}
          onChange={(e) => set(actionId, { single: e.target.value })}
        >
          <option value="">Select template</option>
          {options.length === 0 ? <option disabled>No template defined</option> : null}
          {options.map((template) => (
            <option key={String(template.ID)} value={String(template.ID)}>
              {str(template, 'template_name')}
            </option>
          ))}
        </select>
      </div>
    )
  }

  if (actionKey === 'send_automated_commands') {
    if (commands.length === 0) {
      return (
        <div className="form-group">
          <div className="alert alert-info">
            <i className="fas fa-info-circle" aria-hidden="true"></i> No commands defined
          </div>
        </div>
      )
    }
    return (
      <div className="form-group">
        {commands.map((command) => {
          const value = String(command.ID)
          return (
            <div className="col-md-3 mb-1" key={value}>
              <input
                className="check-command"
                type="checkbox"
                name={'trigger_actions[' + actionId + '][values][]'}
                value={value}
                checked={state.values.includes(value)}
                onChange={(e) =>
                  set(actionId, {
                    values: e.target.checked
                      ? [...state.values.filter((item) => item !== '' && item !== value), value]
                      : state.values.filter((item) => item !== value),
                  })
                }
              />{' '}
              {str(command, 'command_title')}
            </div>
          )
        })}
      </div>
    )
  }

  return null
}

/**
 * Settings > Proactivitee > Email servers, dupa
 * `email_servers/servers_list.php`: cate un panou pliat pe server, cu numele
 * configuratiei, adresa, numele expeditorului si tot blocul SMTP.
 *
 * Pana acum ecranul avea numai primele trei campuri, desi capatul de API le
 * salveaza pe toate - deci SMTP-ul nu se putea configura deloc din v4.
 */
function EmailServersPanel() {
  const query = useQuery(emailServersQuery())
  const client = useQueryClient()
  const [open, setOpen] = useState(-1)
  if (!query.data) return <Spinner />

  const reload = () => client.invalidateQueries({ queryKey: ['settings', 'proactivitee', 'email-servers'] })

  return (
    <>
      <h4>
        <strong>Email servers</strong> settings
      </h4>
      <hr />
      <section className="settings">
        <div className="row">
          <div className="col-md-2">
            <h5 style={{ marginBottom: 20 }}>Triggers Email Servers</h5>
            <button
              className="btn btn-success btn-sm"
              id="new-notifications-server"
              type="button"
              onClick={() => void createEmailServer().then(reload)}
            >
              <i className="fas fa-plus" aria-hidden="true"></i> Add server
            </button>
          </div>
          <div className="col-md-10 notification_servers_container">
            {query.data.servers.length === 0 ? (
              <div className="alert alert-info">No server configured.</div>
            ) : (
              <div className="panel-group" id="accordion" style={{ margin: 0 }} role="tablist">
                {query.data.servers.map((server, index) => (
                  <EmailServerPanel
                    key={String(server.ID)}
                    server={server}
                    index={index}
                    open={open === index}
                    onToggle={() => setOpen(open === index ? -1 : index)}
                    onRemoved={reload}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        <hr className="dotted" />
      </section>
    </>
  )
}

/** Randul serverului, ca perechi de text - asa cum pleaca la salvare. */
function serverFields(server: Record<string, unknown>): Record<string, string> {
  return {
    scheme_name: str(server, 'scheme_name'),
    email_address: str(server, 'email_address'),
    from_name: str(server, 'from_name'),
    enable_smtp: num(server, 'enable_smtp') === 1 ? 'on' : '',
    smtp_user: str(server, 'smtp_user'),
    /* Ca in macheta: opt stelute cand exista o parola, si atat pleaca inapoi
       daca omul n-o atinge. Capatul de API nu o rescrie cand primeste asta. */
    smtp_password: str(server, 'smtp_password') === '' ? '' : '********',
    smtp_host: str(server, 'smtp_host'),
    smtp_port: str(server, 'smtp_port'),
    smtp_timeout: str(server, 'smtp_timeout'),
  }
}

function EmailServerPanel({
  server,
  index,
  open,
  onToggle,
  onRemoved,
}: {
  server: Record<string, unknown>
  index: number
  open: boolean
  onToggle(): void
  onRemoved(): void
}) {
  const [form, setForm] = useState(() => serverFields(server))
  const [saving, setSaving] = useState(false)

  useEffect(() => setForm(serverFields(server)), [server])

  const set = (key: string, value: string) => setForm((current) => ({ ...current, [key]: value }))

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSaving(true)
    try {
      await saveEmailServer({ ...form, server_id: String(server.ID) })
      toast('Settings saved.', 'Server settings', 'success')
    } catch {
      toast('Something went wrong', 'Server settings', 'error')
    }
    setSaving(false)
  }

  return (
    <div className="panel panel-default">
      <div className="panel-heading" role="tab">
        <div className="panel-title font-14">
          <a
            style={{ padding: 0, display: 'inline-block' }}
            role="button"
            href={'#collapse-parsing-' + index}
            aria-expanded={open}
            onClick={(e) => {
              e.preventDefault()
              onToggle()
            }}
          >
            <h5>
              <i className="far fa-envelope" aria-hidden="true"></i> #{index + 1} {str(server, 'scheme_name')}
            </h5>
          </a>
          <button
            type="button"
            className="pull-right btn btn-sm btn-danger remove-server"
            data-server_id={String(server.ID)}
            style={{ marginTop: -4 }}
            onClick={() => void removeEmailServer(num(server, 'ID')).then(onRemoved)}
          >
            Remove
          </button>
        </div>
      </div>
      {open ? (
        <div id={'collapse-parsing-' + index} className="panel-collapse collapse in" role="tabpanel">
          <div className="panel-body">
            <form className="email-server-settings-form" method="post" onSubmit={onSubmit}>
              <div className="form-group row">
                <ServerField label="Configuration name" name="scheme_name" value={form.scheme_name} onChange={set} />
              </div>
              <div className="form-group row">
                <input type="hidden" name="server_id" value={String(server.ID)} readOnly />
                <ServerField
                  label="Email address"
                  name="email_address"
                  type="email"
                  value={form.email_address}
                  onChange={set}
                />
                <ServerField label="From name" name="from_name" value={form.from_name} onChange={set} />
                <div className="col-md-4"></div>
              </div>

              <h5>SMTP Server</h5>
              <hr />
              <div className="form-group row">
                <div className="col-md-4">
                  <input
                    className="icheck-servers-checkbox"
                    type="checkbox"
                    name="enable_smtp"
                    value="on"
                    checked={form.enable_smtp === 'on'}
                    onChange={(e) => set('enable_smtp', e.target.checked ? 'on' : '')}
                  />
                  <label>Enable SMTP protocol</label>
                </div>
                <ServerField label="SMTP User" name="smtp_user" value={form.smtp_user} onChange={set} />
                <ServerField
                  label="SMTP Password"
                  name="smtp_password"
                  type="password"
                  value={form.smtp_password}
                  onChange={set}
                />
                <ServerField label="SMTP Host" name="smtp_host" value={form.smtp_host} onChange={set} />
                <ServerField label="SMTP Port" name="smtp_port" value={form.smtp_port} onChange={set} />
                <ServerField
                  label="SMTP Timeout (in seconds)"
                  name="smtp_timeout"
                  value={form.smtp_timeout}
                  onChange={set}
                />
              </div>
              <div className="aright">
                <button className="btn btn-primary" type="submit" disabled={saving}>
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function ServerField({
  label,
  name,
  value,
  type = 'text',
  onChange,
}: {
  label: string
  name: string
  value: string
  type?: string
  onChange(name: string, value: string): void
}) {
  return (
    <div className="col-md-4">
      <input
        className="form-control input-sm"
        placeholder={label}
        type={type}
        name={name}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
      />
      <span className="help-block">{label}</span>
    </div>
  )
}

/** `starts_at`/`ends_at` vin in UTC; casuta le arata in fusul ferestrei. */
function localInput(value: unknown, timezone: string): string {
  const text = value === null || value === undefined ? '' : String(value)
  if (text === '') return ''
  const when = new Date(text.replace(' ', 'T') + 'Z')
  if (isNaN(when.getTime())) return ''
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone === '' ? 'UTC' : timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(when)
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '00'
  return get('year') + '-' + get('month') + '-' + get('day') + 'T' + get('hour') + ':' + get('minute')
}

function QuietHoursPanel() {
  const query = useQuery(quietHoursQuery())
  const client = useQueryClient()
  const [edited, setEdited] = useState<Record<string, unknown> | null>(null)
  if (!query.data) return <Spinner />
  const { windows, daily, timezone, devices, groups, dayNames, localNow } = query.data
  const reload = () => client.invalidateQueries({ queryKey: ['settings', 'proactivitee', 'quiet'] })
  const onDigest = async () => {
    const form = document.getElementById('qh-daily') as HTMLFormElement
    const data = new FormData(form)
    await quietDigestSave({
      enabled: data.get('enabled') ? '1' : '',
      send_at: String(data.get('send_at') ?? ''),
      timezone: String(data.get('timezone') ?? ''),
      recipients: String(data.get('recipients') ?? ''),
    })
    toast('Daily digest saved.', 'Quiet hours', 'success')
    await reload()
  }
  return (
    <>
      <h4><strong>Quiet hours</strong> &amp; maintenance windows</h4>
      <p className="qh-lead">
        While a window is on, alarms that open are kept quiet: no e-mail, no on-screen message, no automatic ticket, no bell, no AI agent.
        <b> Hush</b> still opens them, so the history is true; <b>Silence</b> does not open them at all - for a planned reboot.
        Whatever you let through (Critical, by default) behaves as usual. When the window ends, a digest says what happened meanwhile.
        Company time zone: <b>{timezone}</b>.
      </p>
      <div className="row">
        <div className="col-md-7">
          <div className="qh-card">
            <h5>Windows in place</h5>
            {windows.length === 0 ? (
              <p className="text-muted" style={{ margin: 0 }}>No window yet. Every alarm is announced the moment it opens, day and night.</p>
            ) : (
              <ul className="qh-list" id="qh-list">
                {windows.map((w) => (
                  <li key={String(w.id)} data-id={String(w.id)}>
                    <div className="qh-when">
                      <b>{str(w, 'name')}{w.is_active ? <span className="qh-now">quiet now</span> : null}</b>
                      <span>{str(w, 'text')}</span>
                      {str(w, 'digest_to').trim() === '' ? null : (
                        <>
                          <br />
                          <span>digest to {str(w, 'digest_to')}</span>
                        </>
                      )}
                    </div>
                    <span className="qh-scope">{str(w, 'scope_label')}</span>
                    <div className="qh-actions">
                      <button type="button" className="btn btn-default btn-xs qh-edit" onClick={() => setEdited(w)}>Edit</button>
                      {w.is_active ? <button type="button" className="btn btn-default btn-xs qh-end" onClick={() => void quietEnd(num(w, 'id')).then(reload)}>End now</button> : null}
                      <button type="button" className="btn btn-default btn-xs qh-delete" onClick={() => { if (confirm('Delete this window?')) void quietDelete(num(w, 'id')).then(reload) }}><i className="fas fa-trash-alt"></i></button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="qh-card">
            <h5>Daily digest</h5>
            <p className="text-muted" style={{ margin: '0 0 10px', fontSize: 12 }}>
              One e-mail a day, at the hour you pick: alarms opened in the last 24 hours by severity, what is still open and since when,
              what the AI agent fixed, which tickets were opened, the machines with the most alarms, certificates under 14 days, backup jobs that failed.
              Also rings the bell in the portal.
            </p>
            <form id="qh-daily" className="form-inline qh-form" onSubmit={(e) => e.preventDefault()}>
              <div className="form-group">
                <label><input type="checkbox" name="enabled" value="1" defaultChecked={num(daily, 'enabled') === 1} /> Send every day at</label>
                <input type="time" className="form-control input-sm" name="send_at" defaultValue={str(daily, 'send_at').slice(0, 5)} style={{ width: 110 }} />
              </div>
              <div className="form-group">
                <label>time zone</label>
                <input type="text" className="form-control input-sm" name="timezone" defaultValue={str(daily, 'timezone') || timezone} style={{ width: 170 }} />
              </div>
              <div className="form-group" style={{ display: 'block', marginTop: 8 }}>
                <label>to</label>
                <input type="text" className="form-control input-sm" name="recipients" defaultValue={str(daily, 'recipients')} placeholder="e-mail addresses, comma separated" style={{ width: '100%', maxWidth: 520 }} />
              </div>
              <button type="button" className="btn btn-success btn-sm" id="qh-daily-save" onClick={() => void onDigest()}><i className="fas fa-check"></i> Save digest</button>
              {str(daily, 'last_sent') === '' ? null : (
                <span className="text-muted" style={{ marginLeft: 8, fontSize: 12 }}>last sent {str(daily, 'last_sent')}</span>
              )}
            </form>
          </div>
        </div>
        <div className="col-md-5">
          <div className="qh-card">
            <h5 id="qh-form-title">{edited === null ? 'New window' : 'Edit: ' + str(edited, 'name')}</h5>
            <QuietWindowForm
              key={edited === null ? 'new' : String(edited.id)}
              edited={edited}
              timezone={timezone}
              devices={devices}
              groups={groups}
              dayNames={dayNames}
              localNow={localNow}
              onReset={() => setEdited(null)}
              onSaved={async () => {
                setEdited(null)
                await reload()
              }}
            />
          </div>
        </div>
      </div>
    </>
  )
}

/**
 * Formularul ferestrei. Se reface din temelii cand se apasa Edit pe alt rand
 * (`key`), ca sa porneasca de la valorile randului ales - butonul Edit lipsea
 * cu totul pana acum, desi ecranul vechi il are, deci o fereastra pusa gresit
 * nu se putea indrepta, doar stearge si scrie din nou.
 */
function QuietWindowForm({
  edited,
  timezone,
  devices,
  groups,
  dayNames,
  localNow,
  onReset,
  onSaved,
}: {
  edited: Record<string, unknown> | null
  timezone: string
  devices: Array<Record<string, unknown>>
  groups: Array<Record<string, unknown>>
  dayNames: Record<string, string>
  localNow: string
  onReset(): void
  onSaved(): Promise<void>
}) {
  const formRef = useRef<HTMLFormElement>(null)
  const [kind, setKind] = useState(edited === null ? '1' : String(num(edited, 'kind') || 1))
  const [scope, setScope] = useState(edited === null ? '1' : String(num(edited, 'scope_type') || 1))
  const [error, setError] = useState('')
  const windowTz = edited === null ? timezone : str(edited, 'timezone') || timezone
  const days = edited === null ? 0 : num(edited, 'days')

  const onSave = async () => {
    setError('')
    const form = formRef.current
    if (!form) return
    const data = new FormData(form)
    try {
      await quietSave({
        id: String(data.get('id') ?? '0'),
        name: String(data.get('name') ?? ''),
        scope_type: String(data.get('scope_type') ?? '1'),
        scope_id: String(data.get('scope_id') ?? '0'),
        scope_id_device: String(data.get('scope_id_device') ?? '0'),
        kind: String(data.get('kind') ?? '1'),
        starts_at: String(data.get('starts_at') ?? ''),
        ends_at: String(data.get('ends_at') ?? ''),
        'days[]': data.getAll('days[]').map(String),
        start_time: String(data.get('start_time') ?? ''),
        end_time: String(data.get('end_time') ?? ''),
        timezone: String(data.get('timezone') ?? ''),
        mode: String(data.get('mode') ?? '1'),
        pass_severity: String(data.get('pass_severity') ?? '3'),
        digest: data.get('digest') ? '1' : '',
        digest_to: String(data.get('digest_to') ?? ''),
      })
      toast('Window saved.', 'Quiet hours', 'success')
      await onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The window could not be saved.')
    }
  }

  return (
    <form id="qh-form" className="qh-form" ref={formRef} onSubmit={(e) => e.preventDefault()}>
      <input type="hidden" name="id" value={edited === null ? '0' : String(edited.id)} readOnly />
      <div className="form-group">
        <label>Name</label>
        <input type="text" className="form-control input-sm" name="name" maxLength={120} defaultValue={edited === null ? '' : str(edited, 'name')} placeholder="Patch Tuesday, Night, Datacenter move…" />
      </div>
      <div className="form-group">
        <label>Applies to</label>
        <div className="form-inline">
          <select className="form-control input-sm" name="scope_type" id="qh-scope" value={scope} onChange={(e) => setScope(e.target.value)}>
            <option value="1">whole company</option>
            <option value="2">an alarm group</option>
            <option value="3">one device</option>
          </select>
          {/* Ca in `syncScope()`: numai lista aratata poarta `scope_id`. */}
          <select className="form-control input-sm" name={scope === '2' ? 'scope_id' : 'scope_id_group'} id="qh-scope-group" defaultValue={edited !== null && num(edited, 'scope_type') === 2 ? String(edited.scope_id) : undefined} style={{ display: scope === '2' ? 'inline-block' : 'none', maxWidth: 220 }}>
            {groups.map((g) => <option key={String(g.ID)} value={String(g.ID)}>{str(g, 'group_name')}</option>)}
          </select>
          <select className="form-control input-sm" name={scope === '3' ? 'scope_id' : 'scope_id_device'} id="qh-scope-device" defaultValue={edited !== null && num(edited, 'scope_type') === 3 ? String(edited.scope_id) : undefined} style={{ display: scope === '3' ? 'inline-block' : 'none', maxWidth: 220 }}>
            {devices.map((d) => <option key={String(d.ID)} value={String(d.ID)}>{str(d, 'Denumire')}</option>)}
          </select>
        </div>
      </div>
      <div className="form-group">
        <label>When</label><br />
        <label style={{ fontWeight: 400 }}><input type="radio" name="kind" value="1" checked={kind === '1'} onChange={() => setKind('1')} /> once (maintenance)</label>
        <label style={{ fontWeight: 400, marginLeft: 12 }}><input type="radio" name="kind" value="2" checked={kind === '2'} onChange={() => setKind('2')} /> every week (quiet hours)</label>
      </div>
      <div id="qh-once" style={{ display: kind === '1' ? 'block' : 'none' }}>
        <div className="form-group form-inline">
          <label>from</label> <input type="datetime-local" className="form-control input-sm" name="starts_at" defaultValue={edited === null ? localNow : localInput(edited.starts_at, windowTz) || localNow} />
          <label style={{ marginLeft: 6 }}>to</label> <input type="datetime-local" className="form-control input-sm" name="ends_at" defaultValue={edited === null ? '' : localInput(edited.ends_at, windowTz)} />
        </div>
      </div>
      <div id="qh-weekly" style={{ display: kind === '2' ? 'block' : 'none' }}>
        <div className="form-group qh-days">
          {Object.entries(dayNames).map(([n, label]) => (
            <label key={n}><input type="checkbox" name="days[]" value={n} defaultChecked={edited === null ? Number(n) <= 5 : (days & (1 << (Number(n) - 1))) !== 0} /> {label}</label>
          ))}
        </div>
        <div className="form-group form-inline">
          <label>from</label> <input type="time" className="form-control input-sm" name="start_time" defaultValue={edited === null ? '22:00' : str(edited, 'start_time').slice(0, 5) || '22:00'} style={{ width: 110 }} />
          <label style={{ marginLeft: 6 }}>to</label> <input type="time" className="form-control input-sm" name="end_time" defaultValue={edited === null ? '06:00' : str(edited, 'end_time').slice(0, 5) || '06:00'} style={{ width: 110 }} />
          <span className="text-muted" style={{ fontSize: 12, marginLeft: 6 }}>past midnight is fine</span>
        </div>
      </div>
      <div className="form-group">
        <label>Time zone</label>
        <input type="text" className="form-control input-sm" name="timezone" defaultValue={windowTz} />
      </div>
      <div className="form-group">
        <label>Mode</label>
        <select className="form-control input-sm" name="mode" defaultValue={edited === null ? '1' : String(num(edited, 'mode') || 1)}>
          <option value="1">Hush - alarms open, nobody is told</option>
          <option value="2">Silence - alarms are not opened at all</option>
        </select>
      </div>
      <div className="form-group">
        <label>Still gets through</label>
        <select className="form-control input-sm" name="pass_severity" defaultValue={edited === null ? '3' : String(num(edited, 'pass_severity'))}>
          <option value="3">Critical</option>
          <option value="2">Major and Critical</option>
          <option value="1">Everything but Info</option>
          <option value="0">Nothing</option>
        </select>
      </div>
      <div className="form-group">
        <label><input type="checkbox" name="digest" value="1" defaultChecked={edited === null ? true : num(edited, 'digest') === 1} /> Digest when the window ends</label>
        <input type="text" className="form-control input-sm" name="digest_to" defaultValue={edited === null ? '' : str(edited, 'digest_to')} placeholder="also e-mail to (comma separated) - the bell rings anyway" />
      </div>
      <button type="button" className="btn btn-success btn-sm" id="qh-save" onClick={() => void onSave()}><i className="fas fa-check"></i> Save window</button>
      <button type="button" className="btn btn-default btn-sm" id="qh-reset" onClick={onReset}>New</button>
      <span id="qh-error" className="text-danger" style={{ display: 'block', marginTop: 8 }}>{error}</span>
    </form>
  )
}
